import { useEffect, useState } from 'react';
import type { ProductDesignerConfig, GlbDesignPlacement, ProductVariantDTO, PrintColorOption, ImageSide } from '@customedge/shared';
import { DEFAULT_LAYOUT_FEATURE_CONFIG, printColorOptionFromString, filterPrintColorOptions, parsePrintAreaSizeOptions } from '@customedge/shared';
import { useDesignStore } from '../store/designStore';

// `?colorVariant=Black` — pre-selects one of the product's vendor Product
// Color Variants (ProductOptionsModal's "Product color" / DesignerHeader's
// Variant pill) at launch time. Matched case-insensitively against
// `colorName` since it's admin free text, same convention as
// getRequestedVariantSelectionsFromSearch's kebab-case match below. Pure/
// DOM-free, same reasoning as every other `?param=` reader here.
export function getRequestedColorVariantNameFromSearch(search: string): string | null {
  const raw = new URLSearchParams(search).get('colorVariant');
  return raw ? raw.trim() : null;
}

// `?printMethod=Digital+Transfer` — pre-selects one of the product's
// admin-configured free-text print methods (ProductConfig.printMethods) at
// launch time. Matched case-insensitively, same reasoning as
// getRequestedColorVariantNameFromSearch above.
export function getRequestedPrintMethodFromSearch(search: string): string | null {
  const raw = new URLSearchParams(search).get('printMethod');
  return raw ? raw.trim() : null;
}

// `?printPositions=voorzijde,achterzijde` — pre-selects MULTIPLE of the
// product's admin-configured free-text print positions (ProductConfig.
// printPositions) at launch time — the grouping dimension ABOVE printMethod.
// Comma-joined, matched case-insensitively, mirrors getRequestedSideNamesFromSearch's
// convention. Plural param name (unlike singular `?printMethod=`) since this
// is genuinely a multiselect, not a single pre-selection.
export function getRequestedPrintPositionsFromSearch(search: string): string[] {
  const raw = new URLSearchParams(search).get('printPositions');
  return raw ? raw.split(',').map(s => s.trim()).filter(Boolean) : [];
}

// `?printMethodByPosition={"voorzijde":"Zeefdruk","achterzijde":"DTF+Transfer"}`
// — URL-encoded JSON, one print-method choice per selected position (see
// getRequestedPrintPositionsFromSearch above). A single global `?printMethod=`
// can't express "Zeefdruk on the front AND DTF Transfer on the back"
// simultaneously, which is the entire point of print positions being a
// multiselect — this sibling param carries the per-position resolution.
// Malformed/missing JSON silently yields {} (permissive-parse convention,
// same as every other `?param=` reader here) rather than rejecting the whole
// launch.
export function getRequestedPrintMethodByPositionFromSearch(search: string): Record<string, string> {
  const raw = new URLSearchParams(search).get('printMethodByPosition');
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const result: Record<string, string> = {};
      for (const [k, v] of Object.entries(parsed)) {
        if (typeof v === 'string') result[k] = v;
      }
      return result;
    }
  } catch { /* fall through to empty */ }
  return {};
}

// `?printColorByPosition={"voorzijde":"2","achterzijde":"full-color"}` —
// same shape/reasoning as getRequestedPrintMethodByPositionFromSearch above,
// for print COLOR instead of method. Returns the RAW string values (not yet
// parsed to PrintColorOption) -- see resolvePrintColorByPosition below for
// why parsing needs to happen later, with product-config context in hand.
export function getRequestedPrintColorByPositionFromSearch(search: string): Record<string, string> {
  const raw = new URLSearchParams(search).get('printColorByPosition');
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const result: Record<string, string> = {};
      for (const [k, v] of Object.entries(parsed)) {
        if (typeof v === 'string') result[k] = v;
      }
      return result;
    }
  } catch { /* fall through to empty */ }
  return {};
}

// Resolves ONE requested color value for a position to a real PrintColorOption.
// Found + fixed 2026-08-05 (TopPoint LT98886, order 000042942 -- user report:
// "auto select print method and colors not apply... some translation not
// matching"): the real PDP (getCustomEdgeVariantParams in the React storefront's
// UploadImages/index.jsx) sends the candidate option's own VALUE TITLE verbatim
// -- e.g. "Lasergravure", "Full colour" -- not a bare number or the literal
// 'full-color' string printColorOptionFromString alone can parse. That parser
// only recognizes digits, "N kleur(en)", and the exact string 'full-color'
// (confirmed live: MidOcean's Dutch "N kleuren" phrasing happens to match the
// KLEUR_PATTERN regex, which is why THAT vendor's colorByPosition worked --
// but a binary None/single-named-value method like TopPoint's "Laser"
// (values: None/"Lasergravure") or "Digital High Gloss" (None/"Full colour",
// note the space+capital vs. the parser's hyphenated lowercase 'full-color')
// has no such match, so the value silently dropped and the color pill/canvas
// state never saw the selection at all -- even though the position and
// method pills resolved correctly). Falls back to the plain parser first
// (cheap, covers the common bare-number/full-color/kleuren cases with no
// config lookup needed), then cross-references the position's own matched
// METHOD's `printColorOptionsTranslated` map (method -> {optString:
// translatedTitle}) to find which option's translated title equals the
// requested value -- inverts the same map DesignerHeader's pill already
// reads forward (title lookup), so a match here is exactly the same
// resolution the pill itself would produce.
function resolveColorForPosition(
  raw: string,
  method: string | undefined,
  colorOptionsTranslated: Record<string, Record<string, string>> | undefined,
  // Found + fixed 2026-08-10 (HIGH PARK AR1470 regression, introduced by the
  // LT95343 fallback below the same day): when a position genuinely HAS
  // area-size tiers (e.g. MidOcean's Digitale transfer, "Full color max 190
  // cm2"), the loose `/^full\s?colou?r/` fallback below would ALSO match it
  // (every area-size label starts with "Full color") and resolve it to the
  // WRONG, generic FULL_COLOR value via this function's method-wide
  // translation map — which only ever holds the smallest tier's label (see
  // resolveAreaSizeForPosition's own doc comment) — before the caller ever
  // got a chance to try the correct, side-specific area-size resolution.
  // Passing the side's own real tier list lets this function refuse to apply
  // the loose fallback whenever a genuine tier match should be attempted
  // instead, restoring the caller's intended try-color-then-try-area-size
  // order without the loose fallback jumping the queue.
  areaSizeOptions: number[] | undefined,
): PrintColorOption | undefined {
  const direct = printColorOptionFromString(raw);
  if (direct != null) return direct;
  if (!method) return undefined;
  const optionMap = colorOptionsTranslated?.[method];
  if (!optionMap) return undefined;
  const reqLower = raw.trim().toLowerCase();
  for (const [optString, translatedTitle] of Object.entries(optionMap)) {
    if (translatedTitle.trim().toLowerCase() === reqLower) {
      return printColorOptionFromString(optString) ?? undefined;
    }
  }
  // Found + fixed 2026-08-10 (LT95343, TopPoint "Digital Transfer" method,
  // candidate value literally "Full colour" -- no area-size suffix at all):
  // the exact-match loop above can never match a plain vendor "Full colour"/
  // "Full color" title against this method's translated 'full-color' entry,
  // because that translation is Magento's own "{method} 0" row, which for
  // area-size-capable methods is worded "Full color max {N} cm2" regardless
  // of whether THIS product actually varies its area by tier (see
  // resolveAreaSizeForPosition's own doc comment on why that suffix exists
  // at all) -- a product with only ONE real full-color choice and no genuine
  // area-size tiers has no cm2 number to strip, so the two strings can never
  // be made equal by normalization alone. Since 'full-color' is the ONLY
  // color-count-shaped option method conventions in this catalog ever offer
  // (see NONE_VALUE_TITLES/candidateMethodOptions on the Magento side --
  // every non-None candidate value is either a color count or full-color,
  // never both), a raw request that itself reads as "full colour"/"full
  // color" (with or without a trailing size clause) can safely resolve to
  // this method's OWN full-color entry whenever this method's translated map
  // has exactly one entry that itself starts with "full colour"/"full
  // color" -- narrower than a blanket substring match, so a method with
  // several real spot-color tiers (which never start with "full colour")
  // can't accidentally match here. GATED to only fire when this position has
  // NO real area-size tiers of its own -- otherwise a genuine tier request
  // like "Full color max 190 cm2" would ALSO match this same regex (every
  // tier label starts with "Full color") and get silently mis-resolved to
  // the generic FULL_COLOR value instead of the specific tier the caller
  // should fall through to resolveAreaSizeForPosition for.
  if (!areaSizeOptions?.length && /^full\s?colou?r/.test(reqLower)) {
    for (const [optString, translatedTitle] of Object.entries(optionMap)) {
      if (/^full\s?colou?r/.test(translatedTitle.trim().toLowerCase())) {
        return printColorOptionFromString(optString) ?? undefined;
      }
    }
  }
  return undefined;
}

// Resolves ONE requested color value for a position to a real area-size tier
// (cm2), for methods like MidOcean's Digitale transfer that have physical
// print-size tiers instead of a color-count concept. Found + fixed 2026-08-10
// (HIGH PARK AR1470, order search.promofit.nl -- PDP sent "Full color max 190
// cm2" for the FRONT POCKET/FRONT TRANSFER/TOP positions, but the header
// popup never auto-selected it). Root cause: resolveColorForPosition above
// can ONLY match against `printColorOptionsTranslated`, which for a
// full-color method always contains exactly ONE entry -- the Magento
// "{method} 0" translation, i.e. always the SMALLEST tier's label (e.g.
// "Full color max 50 cm2") -- since that translation table was built for the
// color-COUNT model (one real "full-color" option per method) and has no way
// to represent "this side's tier index 2 is 190 cm2" per-position. Area-size
// tiers, unlike color counts, are genuinely different per POSITION for the
// same method (see ProductOptionsModal.tsx's own sideAreaSizeOptionsOverride
// comment) — so unlike resolveColorForPosition's method-wide translation
// lookup, this must be matched against the SPECIFIC side's own
// printAreaSizeOptions list, built directly from the literal
// "Full color max {N} cm2" string ProductOptionsModal.tsx/BackgroundColorTool.tsx
// already use to label these chips (confirmed this is Magento's own exact
// stored value format, per the area-size feature's own launch notes) — no
// DB/translation lookup involved, since area-size tiers have no separate
// vendor-language label to translate, only the raw cm2 number.
function resolveAreaSizeForPosition(
  raw: string,
  areaSizeOptions: number[] | undefined,
): number | undefined {
  if (!areaSizeOptions?.length) return undefined;
  const reqLower = raw.trim().toLowerCase();
  for (const size of areaSizeOptions) {
    if (`full color max ${size} cm2`.toLowerCase() === reqLower) return size;
  }
  return undefined;
}

// `?printColor=2` or `?printColor=full-color` — pre-selects one of the
// product's resolved printColorOptions at launch time (distinct from
// `?printColors=` above, which NARROWS the admin's whole set; this picks a
// single one of whatever set results). Reuses the same
// printColorOptionFromString parser `?printColors=` uses per-entry.
export function getRequestedPrintColorFromSearch(search: string): PrintColorOption | null {
  const raw = new URLSearchParams(search).get('printColor');
  return raw ? printColorOptionFromString(raw) : null;
}

// `?sides=front,back` (comma-separated, lowercase) restricts the designer to
// just those photos/sides, matched case-insensitively against each side's
// `label` (there's no separate slug field on ImageSideConfig). Absent or
// empty → every side loads, same as before this param existed.
// Pure/DOM-free (search passed in) so this is unit-testable without mocking
// `window` — same reasoning as isPopupModeFromSearch in useDesignerInit.ts.
export function getRequestedSideNamesFromSearch(search: string): string[] | null {
  const raw = new URLSearchParams(search).get('sides');
  if (!raw) return null;
  const names = raw.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
  return names.length ? names : null;
}

function getRequestedSideNames(): string[] | null {
  return getRequestedSideNamesFromSearch(window.location.search);
}

// `?printColors=full-color,1,2` — narrows the admin-configured
// `config.printColorOptions` set at launch time (e.g. a storefront placement
// that only supports a subset). Invalid entries (anything not 'full-color'
// or an integer 1-10) are silently dropped rather than rejecting the whole
// param — same permissive-parse convention as every other `?param=` reader
// here. Pure/DOM-free (search string passed in), same reasoning as
// getRequestedSideNamesFromSearch above.
export function getRequestedPrintColorOptionsFromSearch(search: string): PrintColorOption[] | null {
  const raw = new URLSearchParams(search).get('printColors');
  if (!raw) return null;
  const parsed = raw.split(',').map(printColorOptionFromString).filter((o): o is PrintColorOption => o !== null);
  return parsed.length ? parsed : null;
}

// Both a variant's own label ("Color", "Front Panel Material") and an option's
// name ("Red", "Navy Blue") are free text set by the admin — kebab-case both
// sides before comparing so `?variant_front-panel-material=navy-blue` matches
// regardless of spacing/casing in the source data.
export function toKebab(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, '-');
}

// `?variant_color=red&variant_material=leather` — one `variant_{label}` param
// per variant group, kebab-cased. Returns a map of kebab(variantLabel) →
// kebab(optionName), read once at config-load time in useProductSetup below.
// Pure/DOM-free, same reasoning as getRequestedSideNamesFromSearch above.
export function getRequestedVariantSelectionsFromSearch(search: string): Record<string, string> {
  const params = new URLSearchParams(search);
  const selections: Record<string, string> = {};
  for (const [key, value] of params.entries()) {
    if (!key.startsWith('variant_') || !value) continue;
    selections[key.slice('variant_'.length)] = toKebab(value);
  }
  return selections;
}

// Resolves each product variant to a selected option id: the `?variant_*`
// param's matching option if present, else the first available option — a
// variant with a matched-but-nonexistent option name also falls back to the
// first option rather than left unselected, since "auto-select the 1st
// variant" is the explicit fallback for anything not resolvable from the URL.
export function resolveInitialVariantSelections(
  variants: ProductVariantDTO[],
  requested: Record<string, string>,
): Record<string, string> {
  const selections: Record<string, string> = {};
  for (const variant of variants) {
    if (!variant.options.length) continue;
    const requestedOptionName = requested[toKebab(variant.label)];
    const matched = requestedOptionName
      ? variant.options.find(o => toKebab(o.name) === requestedOptionName)
      : undefined;
    selections[variant.id] = (matched ?? variant.options[0]).id;
  }
  return selections;
}

// Matches a customer-requested position/method string against the product's
// own configured list, trying the raw value first and falling back to each
// entry's Magento-TRANSLATED label (see backend/src/services/
// translationLookup.ts). Found + fixed 2026-08-05 via a real live order
// (Clipper SKU 265555): Magento's own custom-option UI shows this vendor's
// position labels in Dutch ("Paneel 1") via its translation layer, but
// CustomEdge stores the raw vendor-feed value in English ("Panel 1") --
// PFConcept's own vendor data happens to already be Dutch at the source
// (`subgroup_label` values like "voorzijde" match verbatim), which is why
// this gap was invisible until a vendor whose raw feed language differs from
// Magento's translated display language was actually tested. A raw-only
// match left `matchedPositions`/`matchedPrintMethod` empty for every
// Clipper (and likely GivingEurope/MidOcean/TopPoint) product whose
// position/method names get Dutch-translated on the Magento side, so the
// designer opened with NO position/method filtering active at all --
// showing every side ungated, exactly as if nothing had been pre-selected.
function matchConfiguredValue(
  requested: string,
  configured: string[],
  translated: Record<string, string> | undefined,
): string | undefined {
  const req = requested.toLowerCase();
  const rawMatch = configured.find(c => c.toLowerCase() === req);
  if (rawMatch) return rawMatch;
  if (!translated) return undefined;
  return configured.find(c => translated[c]?.toLowerCase() === req);
}

// Read-only preview/download consumers (DesignPreviewCanvas, DesignDownloadRoot)
// have no `?colorVariant=`/`?printMethod=` URL params to read — they load a
// SAVED design by id, whose own selections live in the design row itself
// (productInfo.colorVariantId / printingMethodDetails), fetched separately
// and handed in here. When present, these take priority over the URL-param
// path below so a saved selection is never silently overridden by the
// "auto-select first variant" fallback that path exists to skip.
export interface ProductSetupOverrides {
  colorVariantId?: string | null;
  printMethod?: string | null;
  // Same override-wins-over-URL-param precedence as colorVariantId/printMethod
  // above — added 2026-08-06 for the read-only preview consumers this comment
  // block already anticipated (DesignPreviewCanvas.tsx, shared by
  // DesignWidgetRoot.tsx's Magento-embeddable `?mode=widget` iframe and
  // ApprovalPreview.tsx's customer approval page). Both fetch a saved
  // design's own `positions`/`methodByPosition`/`colorByPosition` from
  // printingMethodData server-side and need to feed them in here — without
  // this, `useProductSetup`'s position filter only ever reads
  // `?printPositions=`/`?printMethodByPosition=`/`?printColorByPosition=`
  // from `window.location.search`, which neither of those two read-only
  // routes ever sets, so the filter never engages and every side/print-method
  // on the product renders regardless of what the customer actually selected.
  // Reported live: the Magento React storefront's own embedded preview widget
  // (which loads via DesignWidgetRoot) kept showing every image even after
  // the in-designer Preview tab was fixed to show only selected positions —
  // this is the reason: two different loaders, only one of them fixed.
  printPositions?: string[];
  printMethodByPosition?: Record<string, string>;
  printColorByPosition?: Record<string, PrintColorOption>;
}

export function useProductSetup(config: ProductDesignerConfig | null, overrides?: ProductSetupOverrides) {
  const {
    setSides, setProductConfig, setLayoutConfig, setVariants, setSelectedVariantOptions,
    setColorVariants, activeColorVariantId, setActiveColorVariant, selectedPrintMethod,
    setSelectedPrintMethod, setSelectedPrintColorOption,
    selectedPrintPositions, setSelectedPrintPositions,
    selectedPrintMethodByPosition, setSelectedPrintMethodByPosition,
    setSelectedPrintColorByPosition, setSelectedPrintAreaSizeByPosition,
  } = useDesignStore();

  useEffect(() => {
    if (!config) return;

    // `?printColors=` can only narrow the admin's own set, never widen it —
    // see filterPrintColorOptions's own comment. No override (param absent /
    // fully invalid) keeps the admin-configured set exactly as the backend
    // resolved it.
    const requestedPrintColors = getRequestedPrintColorOptionsFromSearch(window.location.search);
    const printColorOptions = requestedPrintColors
      ? filterPrintColorOptions(requestedPrintColors, config.printColorOptions) ?? config.printColorOptions
      : config.printColorOptions;
    setProductConfig({ ...config, printColorOptions });
    setLayoutConfig(config.layout?.featureConfig ?? DEFAULT_LAYOUT_FEATURE_CONFIG);
    const variants = config.variants ?? [];
    setVariants(variants);
    // Every variant gets a selection here — either the `?variant_{label}=`
    // param's matching option, or (no param / no match) the first option, so
    // the 3D mini-preview and 3D Preview tab never render with an unstyled
    // model on initial load. `useSharedDesignLoader`'s later restore of a
    // saved design's own selections (via the same setter, full-replace not
    // merge) correctly overrides these defaults when `?design=` is present.
    setSelectedVariantOptions(
      resolveInitialVariantSelections(variants, getRequestedVariantSelectionsFromSearch(window.location.search))
    );

    // Vendor Product Color Variants (distinct from the 3D `variants` above) —
    // `?colorVariant=Black` (case-insensitive name match) explicitly
    // pre-selects one, exactly as if the customer had already applied it in
    // ProductOptionsModal (`explicit: true` — DesignerHeader's Variant pill
    // shows the resolved name, not its placeholder). Absent/unmatched param
    // falls back to auto-selecting the first one so the canvas never opens on
    // an empty photo set — that fallback stays `explicit: false` so the pill
    // still shows "Select Variant" for a customer who didn't ask for a
    // specific color. A product with none configured gets `colorVariants: []`
    // and this whole block is a no-op — byte-identical to before this
    // feature existed.
    const colorVariants = config.colorVariants ?? [];
    setColorVariants(colorVariants);
    // A saved design's own colorVariantId (read-only preview/download) wins
    // over the URL param — a `?colorVariant=` name match is still checked as
    // a fallback so a fresh (never-saved) launch from Magento's PDP still
    // works exactly as before.
    const requestedColorVariant = overrides?.colorVariantId
      ? colorVariants.find(cv => cv.id === overrides.colorVariantId)
      : (() => {
          const requestedColorVariantName = getRequestedColorVariantNameFromSearch(window.location.search);
          return requestedColorVariantName
            ? colorVariants.find(cv => cv.colorName.toLowerCase() === requestedColorVariantName.toLowerCase())
            : undefined;
        })();
    if (requestedColorVariant && requestedColorVariant.id !== activeColorVariantId) {
      setActiveColorVariant(requestedColorVariant.id, true);
    } else if (colorVariants.length > 0 && !colorVariants.some(cv => cv.id === activeColorVariantId)) {
      setActiveColorVariant(colorVariants[0].id, false);
    } else if (colorVariants.length === 0 && activeColorVariantId !== null) {
      setActiveColorVariant(null, false);
    }

    // `?printMethod=`/`?printColor=` — same "explicit pre-selection" pattern
    // as `?colorVariant=` above: pre-fills DesignerHeader's remaining two
    // pills with a real value (not a placeholder) at launch. No auto-select
    // fallback for these two (unlike color variants, no canvas rendering
    // depends on either being set on load), so an absent/unmatched param just
    // leaves them unset, same as before this feature existed. Matched against
    // `config.printMethods`/the resolved `printColorOptions` above (not a
    // narrower/looser set) so an invalid or out-of-range request is silently
    // ignored rather than forcing a bad value into the store.
    // Same override-wins-over-URL-param precedence as colorVariantId above.
    const requestedPrintMethod = overrides?.printMethod ?? getRequestedPrintMethodFromSearch(window.location.search);
    const matchedPrintMethod = requestedPrintMethod
      ? matchConfiguredValue(requestedPrintMethod, config.printMethods ?? [], config.printMethodsTranslated)
      : undefined;
    if (matchedPrintMethod) setSelectedPrintMethod(matchedPrintMethod);

    // `?printPositions=`/`?printMethodByPosition=` — same "explicit
    // pre-selection" pattern, generalized to a multiselect. Matched
    // case-insensitively against config.printPositions (the admin chip
    // list), same as printMethod above; unmatched entries are silently
    // dropped rather than rejecting the whole param. Same override-wins-
    // over-URL-param precedence as colorVariantId/printMethod above —
    // `overrides.printPositions` (a saved design's OWN canonical positions,
    // not a URL param) still goes through matchConfiguredValue below like
    // everything else, since matching raw-against-raw is a safe no-op there.
    const requestedPrintPositions = overrides?.printPositions ?? getRequestedPrintPositionsFromSearch(window.location.search);
    const configuredPositions = config.printPositions ?? [];
    // Maps each REQUESTED position label (may be Magento's own translated
    // label, e.g. "Paneel 1") to the CANONICAL raw value CustomEdge actually
    // stores (e.g. "Panel 1") -- needed below to re-key
    // methodByPosition/colorByPosition, which arrive keyed by the same
    // requested (possibly-translated) label, not the canonical one.
    const positionKeyMap = new Map<string, string>();
    for (const rp of requestedPrintPositions) {
      const matched = matchConfiguredValue(rp, configuredPositions, config.printPositionsTranslated);
      if (matched) positionKeyMap.set(rp, matched);
    }
    const matchedPositions = [...positionKeyMap.values()];
    if (matchedPositions.length) setSelectedPrintPositions(matchedPositions);

    // Found + fixed 2026-08-05 (Clipper SKU 265555, real live order): Magento
    // sends the customer's ACTUAL selected method name, which for a vendor
    // whose own labels differ from CustomEdge's raw stored value (Clipper's
    // opaque vendor codes, e.g. "TSU", vs. Magento's real configured name
    // "Zeefdruk") never matched `config.printMethods` at all -- every
    // position resolved via positionKeyMap above still ended up with NO
    // matching method, so the designer's method/color pills stayed stuck on
    // their placeholder even after the position fix. Every position key AND
    // every method value now go through matchConfiguredValue (raw-or-
    // translated) before being stored, canonicalizing both sides the same
    // way the top-level printPosition/printMethod matches above already do.
    // Same override-wins-over-URL-param precedence as colorVariantId/printMethod
    // above — added 2026-08-06 so DesignPreviewCanvas's read-only consumers
    // (DesignWidgetRoot's Magento-embeddable widget, ApprovalPreview) can feed
    // in a saved design's own positions/methodByPosition/colorByPosition
    // instead of relying on window.location.search, which neither of those
    // routes ever sets. An override's method/color values are already the
    // canonical values CustomEdge itself saved (not a raw Magento label), so
    // they're applied directly, skipping matchConfiguredValue/
    // resolveColorForPosition's raw-label parsing entirely.
    if (overrides?.printMethodByPosition) {
      setSelectedPrintMethodByPosition(overrides.printMethodByPosition);
    }
    if (overrides?.printColorByPosition) {
      setSelectedPrintColorByPosition(overrides.printColorByPosition);
    }

    if (!overrides?.printMethodByPosition || !overrides?.printColorByPosition) {
      const requestedMethodByPosition = getRequestedPrintMethodByPositionFromSearch(window.location.search);
      const configuredMethods = config.printMethods ?? [];
      const matchedMethodByPosition: Record<string, string> = {};
      for (const [requestedPosition, requestedMethod] of Object.entries(requestedMethodByPosition)) {
        const canonicalPosition = positionKeyMap.get(requestedPosition) ?? requestedPosition;
        const matchedMethod = matchConfiguredValue(requestedMethod, configuredMethods, config.printMethodsTranslated);
        if (matchedMethod) matchedMethodByPosition[canonicalPosition] = matchedMethod;
      }
      if (!overrides?.printMethodByPosition && Object.keys(matchedMethodByPosition).length) {
        setSelectedPrintMethodByPosition(matchedMethodByPosition);
      }

      // Same re-keying as methodByPosition above -- but colorByPosition's own
      // RAW values now need resolving too (see resolveColorForPosition's own
      // doc comment), using this position's own matched method (from
      // matchedMethodByPosition above, falling back to the requested position's
      // still-raw method if this position's method didn't resolve) to look up
      // the right printColorOptionsTranslated entry.
      if (!overrides?.printColorByPosition) {
        const requestedColorByPosition = getRequestedPrintColorByPositionFromSearch(window.location.search);
        const matchedColorByPosition: Record<string, PrintColorOption> = {};
        // Area-size tiers (e.g. MidOcean's Digitale transfer) are merged into
        // the SAME printColorByPosition URL param/payload as color counts
        // (see ProductOptionsModal.tsx's syncSelectionsToUrl/notifyParent —
        // "Full color max {N} cm2" is written into printColorByPosition, not
        // a separate param), so a value that fails color-count resolution
        // must also be tried against this position's own area-size tiers
        // before being dropped -- see resolveAreaSizeForPosition's own doc
        // comment for why this needs the SIDE's real tier list rather than
        // reusing printColorOptionsTranslated like the color-count path does.
        const matchedAreaSizeByPosition: Record<string, number> = {};
        const allImageSides = [...(config.imageSides ?? []), ...colorVariants.flatMap(cv => cv.imageSides)];
        for (const [requestedPosition, rawColor] of Object.entries(requestedColorByPosition)) {
          const canonicalPosition = positionKeyMap.get(requestedPosition) ?? requestedPosition;
          const methodForPosition = matchedMethodByPosition[canonicalPosition] ?? requestedMethodByPosition[requestedPosition];
          const sideForPosition = allImageSides.find(
            s => s.printPosition === canonicalPosition && s.printMethod === methodForPosition,
          );
          const sideAreaSizeOptions = sideForPosition?.printAreaSizeOptions
            ? parsePrintAreaSizeOptions(sideForPosition.printAreaSizeOptions)
            : undefined;
          const resolved = resolveColorForPosition(rawColor, methodForPosition, config.printColorOptionsTranslated, sideAreaSizeOptions);
          if (resolved != null) {
            matchedColorByPosition[canonicalPosition] = resolved;
            continue;
          }
          const resolvedAreaSize = resolveAreaSizeForPosition(rawColor, sideAreaSizeOptions);
          if (resolvedAreaSize != null) matchedAreaSizeByPosition[canonicalPosition] = resolvedAreaSize;
        }
        if (Object.keys(matchedColorByPosition).length) {
          setSelectedPrintColorByPosition(matchedColorByPosition);
        }
        if (Object.keys(matchedAreaSizeByPosition).length) {
          setSelectedPrintAreaSizeByPosition(matchedAreaSizeByPosition);
        }
      }
    }

    const requestedPrintColor = getRequestedPrintColorFromSearch(window.location.search);
    if (requestedPrintColor != null && printColorOptions.includes(requestedPrintColor)) {
      setSelectedPrintColorOption(requestedPrintColor);
    }

    // Auto-load pre-loaded template if configured. `jsonObjects` on this
    // response is a decoded plain object (see backend templates.ts's
    // GET /:id) — was previously read as the nonexistent `jsonObjectsParsed`
    // field, so this auto-load silently never fired for any product with a
    // preLoadedTemplateId configured.
    if (config.preLoadedTemplateId) {
      fetch(`/api/templates/${config.preLoadedTemplateId}`)
        .then(r => r.ok ? r.json() : null)
        .then(template => {
          if (template?.jsonObjects) {
            useDesignStore.getState().loadFromTemplate(template.jsonObjects);
          }
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, overrides?.colorVariantId, overrides?.printMethod]);

  // GET /:sku/config only sends full imageSides/areas for ONE color variant
  // (areasLoaded: true) — every other variant arrives with real metadata but
  // areas: [] on each side, to keep that initial response small for products
  // with 100+ variants (see products.ts's own comment). This cache holds the
  // full sides fetched on demand for whichever OTHER variant(s) the customer
  // actually switches to, keyed by variant id so a variant already fetched
  // this session doesn't get re-fetched on switching back to it.
  const [fetchedVariantSides, setFetchedVariantSides] = useState<Record<string, ImageSide[]>>({});

  useEffect(() => {
    if (!config?.productSku || !activeColorVariantId) return;
    const variant = config.colorVariants?.find(cv => cv.id === activeColorVariantId);
    if (!variant || variant.areasLoaded || fetchedVariantSides[variant.id]) return;

    let cancelled = false;
    fetch(`/api/products/${config.productSku}/color-variants/${variant.id}/sides`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`status ${r.status}`)))
      .then((data: { imageSides: ImageSide[] }) => {
        if (!cancelled) setFetchedVariantSides(prev => ({ ...prev, [variant.id]: data.imageSides }));
      })
      .catch(() => {
        // Leave this variant absent from the cache — the sides-derivation
        // effect below falls back to areas: [] (its pre-existing zero-areas
        // placeholder-box behavior) rather than the canvas staying blank
        // forever. A transient failure surfaces as a wrongly-sized
        // placeholder, not a hang; switching color again retries the fetch.
      });
    return () => { cancelled = true; };
  }, [config?.productSku, config?.colorVariants, activeColorVariantId, fetchedVariantSides]);

  // Sides derivation is a SEPARATE effect, re-run whenever the active color
  // variant OR the selected print method changes (not just when `config`
  // changes) — switching "Product color"/"Print method" in the options popup
  // must re-derive `sides` so the LIVE canvas (DesignerWorkspace renders
  // directly off `sides`/`activeSideKey`, no separate preview-only data path)
  // swaps to just that selection's sides, not just a read-only Preview tab
  // overlay.
  useEffect(() => {
    if (!config) return;

    const activeVariant = config.colorVariants?.find(cv => cv.id === activeColorVariantId);
    // Overlay this session's on-demand fetch (above) onto the variant the
    // /config response already sent — a no-op for whichever variant /config
    // itself resolved as active (areasLoaded: true, never enters
    // fetchedVariantSides), real data for any OTHER variant once its fetch
    // resolves, and the original areas: [] stub in between.
    const resolvedActiveVariant = activeVariant && fetchedVariantSides[activeVariant.id]
      ? { ...activeVariant, imageSides: fetchedVariantSides[activeVariant.id] }
      : activeVariant;
    // A variant row can exist with no photos configured yet (e.g. an admin
    // added the color before uploading its sides) — treating that as "this
    // variant has zero sides" would contradict the auto-select-first-variant
    // fallback above, whose whole point is that the canvas never opens on an
    // empty photo set. Fall back to the base product's sides so an
    // incompletely-configured variant degrades to showing the base photos
    // instead of a blank canvas.
    const sourceImageSides = resolvedActiveVariant && resolvedActiveVariant.imageSides.length > 0
      ? resolvedActiveVariant.imageSides
      : config.imageSides;

    const requestedSideNames = getRequestedSideNames();
    const bySideNameParam = requestedSideNames
      ? sourceImageSides.filter(side => requestedSideNames.includes(side.label.toLowerCase()))
      : sourceImageSides;

    // A side with no printPosition tag is position-agnostic — always shown,
    // regardless of which position(s) the customer selects (opt-in per side,
    // same convention as printMethod). Only sides explicitly tagged with a
    // position NOT in the selected set are hidden. Independent of the method
    // filter below — a side is shown only if it passes BOTH.
    const byPositionParam = selectedPrintPositions.length
      ? bySideNameParam.filter(side => !side.printPosition || selectedPrintPositions.includes(side.printPosition))
      : bySideNameParam;

    // Resolves which print method applies to a given side: when positions are
    // active, each position can have its OWN independently-selected method
    // (e.g. Zeefdruk on voorzijde, DTF Transfer on achterzijde
    // simultaneously) — a single global selectedPrintMethod can't express
    // that. A side with no printPosition (position-agnostic) or a position
    // with no per-position entry falls back to the global selectedPrintMethod
    // unchanged, so a product that never configures positions at all behaves
    // byte-identically to before this feature existed.
    //
    // FIXED (PXK6554, live report 2026-10-28): a position that IS in
    // selectedPrintPositions but has no entry in selectedPrintMethodByPosition
    // (and no global selectedPrintMethod either — e.g. the customer added the
    // position tab but hasn't picked its method chip yet, or a
    // printMethodByPosition URL param omits one position) used to fall
    // through to `null`, which the method filter below treats as "method
    // doesn't matter, show every method's side" — correct for a
    // position-agnostic side, but wrong for a position the customer
    // explicitly selected: it rendered one thumbnail PER METHOD (e.g. 4)
    // instead of exactly one. Once a position is actively selected, default
    // its method to the first one actually available for that position so
    // each selected position still contributes exactly one side, same as a
    // position with an explicit method.
    function effectiveMethodFor(side: { printPosition?: string }): string | null {
      if (side.printPosition && selectedPrintMethodByPosition[side.printPosition]) {
        return selectedPrintMethodByPosition[side.printPosition];
      }
      if (side.printPosition && selectedPrintPositions.includes(side.printPosition) && !selectedPrintMethod) {
        const firstMethodForPosition = sourceImageSides.find(
          s => s.printPosition === side.printPosition && s.printMethod,
        )?.printMethod;
        if (firstMethodForPosition) return firstMethodForPosition;
      }
      return selectedPrintMethod;
    }

    // A side with no printMethod tag is method-agnostic — always shown,
    // regardless of the customer's pick (opt-in per side, same convention as
    // colorVariantId; see ImageSide.printMethod's own comment). Only sides
    // explicitly tagged with a DIFFERENT method than the one selected (for
    // THIS side's own position, see effectiveMethodFor above) are hidden.
    const imageSides = byPositionParam.filter(side => {
      const method = effectiveMethodFor(side);
      return !method || !side.printMethod || side.printMethod === method;
    });

    // A photo/side can have more than one design area (e.g. a "Logo" box and a
    // separate "Text" box on the same product photo) — emit one DesignSide
    // (one independent Fabric canvas) per area, not just the first, sharing
    // `photoId` so the workspace can still show every sibling area's guide box
    // on that photo and the side-tab strip can list them all. Reads each
    // side's own `areas` directly (works identically for a base-product side
    // or a color variant's side) rather than cross-referencing the separate
    // flat `designAreas` list, which only ever covered base-product sides.
    // Prefers the backend's already-localized printPosition/printMethod
    // labels (translations.ts's TranslationBatch, resolved server-side in
    // products.ts) over the stored `label`/`dims.label` string, which several
    // vendor loaders bake raw vendor identifiers into for disambiguation
    // (e.g. GivingEurope's `po_1008892`, Clipper's `[area.code]` fallback,
    // or an untranslated English/uppercase vendor position name) — see the
    // vendor-xml-product-import skill's loader conventions. Falls back to
    // the raw value only when no translated field exists (position-agnostic
    // sides, or a vendor/value with no translation row).
    //
    // CORRECTED (found via live verification, Clipper SKU 267624): a plain
    // `printPositionTranslated || printMethodTranslated` short-circuits on
    // position alone, so two sides sharing one position but differing only
    // by print method (e.g. "Front pocket" TSC vs DTR300) BOTH collapsed to
    // the identical "Voorzijde vakje" label — the vendor-code leak was gone,
    // but so was the disambiguation that label existed for in the first
    // place. Precompute which translated position labels are NOT unique
    // across this photo-group's own sides first, and only fall through to
    // appending the method name for those — a side whose position IS unique
    // keeps the clean, undecorated translated label.
    const positionLabelCounts = new Map<string, number>();
    for (const side of imageSides) {
      const key = side.printPositionTranslated;
      if (key) positionLabelCounts.set(key, (positionLabelCounts.get(key) ?? 0) + 1);
    }
    function displaySideLabel(side: { label: string; printPosition?: string; printPositionTranslated?: string; printMethod?: string; printMethodTranslated?: string }): string {
      const position = side.printPositionTranslated;
      if (position) {
        const isAmbiguous = (positionLabelCounts.get(position) ?? 0) > 1;
        if (!isAmbiguous) return position;
        return side.printMethodTranslated ? `${position} (${side.printMethodTranslated})` : position;
      }
      return side.printMethodTranslated || stripVendorIdSuffix(side.label);
    }

    // Some vendor loaders disambiguate a label collision by appending the
    // vendor's own internal identifier in parens/brackets (GivingEurope's
    // `(po_1008892)` unique-constraint fallback, Clipper's `[area.code]`
    // last-resort fallback — see load-vendor-givingeurope.ts/
    // load-vendor-clipper.ts) rather than a human-readable word. Strips that
    // suffix for display only; the stored label/area_id are unaffected, so
    // admin screens that need the raw disambiguator still see it.
    function stripVendorIdSuffix(label: string): string {
      return label.replace(/\s*[([](?:po_|area_)?[a-zA-Z0-9_]*\d{4,}[)\]]\s*$/, '').trim() || label;
    }

    const sides = imageSides.flatMap(side => {
      const allAreas = side.areas ?? [];

      // A side whose OWN printMethod is null can still have per-area methods
      // stamped by the loader (e.g. GE1001763's "on the lid" photo shared by
      // TB and DO4 — see load-vendor-givingeurope.ts's buildDesignAreaJson).
      // Filter to just the area(s) matching selectedPrintMethod so the
      // customer's print-method pick actually switches which placeholder is
      // shown, instead of always rendering every method's area as a separate,
      // near-identically-labeled tab. Falls back to all areas when none of
      // them carry a printMethod (unrelated products, unaffected), no method
      // is selected yet, OR the filter would leave zero areas -- a
      // method-agnostic side (shown regardless of selection, per the
      // imageSides filter above) can legitimately offer NONE of the
      // customer's selected method on ITS OWN areas (e.g. "on the lid" only
      // has TB/DO4, customer picked LC for the product's OTHER side,
      // "front side"). Filtering to zero there used to fall through to the
      // areas.length===0 branch below -- a placeholder 400x400 box at (0,0)
      // meant for genuinely area-less sides, not "every real area got
      // filtered out" -- landing a small, wrongly-positioned box in the
      // canvas corner instead of showing this side's real, correctly-placed
      // areas. Confirmed live 2026-07-21 on GE1001763 (?printMethod=LC).
      const sideMethod = effectiveMethodFor(side);
      const areaHasOwnMethod = allAreas.some((a: any) => (a.designArea ?? a).printMethod);
      const methodFilteredAreas = sideMethod && areaHasOwnMethod
        ? allAreas.filter((a: any) => {
            const areaMethod = (a.designArea ?? a).printMethod;
            return !areaMethod || areaMethod === sideMethod;
          })
        : allAreas;
      const areas = methodFilteredAreas.length > 0 ? methodFilteredAreas : allAreas;

      if (areas.length === 0) {
        return [{
          sideKey:      `${side.id}-0`,
          photoId:      side.id,
          label:        displaySideLabel(side),
          photoLabel:   displaySideLabel(side),
          placeholderLabel: undefined as string | undefined,
          imagePath:    side.imagePath,
          canvasWidth:  400,
          canvasHeight: 400,
          areaLeft:     0,
          areaTop:      0,
          designAreas:  [],
          glbBox:       undefined as GlbDesignPlacement | undefined,
          printColorOptions: side.printColorOptions,
          printPosition: side.printPosition,
          printAreaSizeOptions: side.printAreaSizeOptions,
        }];
      }

      return areas.map((area: any, i: number) => {
        const dims = area.designArea ?? area;
        // `SelectionArea.designArea` is stored as free-text JSON — some rows
        // (e.g. manually admin-created products, not the numeric test
        // fixtures) persist width/height/left/top as strings. Coerce here so
        // downstream consumers (the save payload's Zod-validated
        // imageWidth/imageHeight, canvas sizing math) always see numbers.
        const width  = Number(dims.width);
        const height = Number(dims.height);
        const left   = Number(dims.left);
        const top    = Number(dims.top);
        const sideLabel = displaySideLabel(side);
        const areaLabel = dims.label ? stripVendorIdSuffix(dims.label) : undefined;
        return {
          sideKey:      `${side.id}-${area.id ?? i}`,
          photoId:      side.id,
          label:        areaLabel && areaLabel !== sideLabel ? `${sideLabel} — ${areaLabel}` : sideLabel,
          photoLabel:   sideLabel,
          // Uses `allAreas.length` (the photo's TOTAL placeholder count), not the
          // post-print-method-filter `areas.length` — a photo with 5 stacked
          // areas (GivingEurope's "at the side" photo: LD/LE/LR1/LR2/LC) still
          // has each area's own real per-method name once selectedPrintMethod
          // narrows the visible set down to 1; using the filtered count made
          // this fall back to the photo's combined `side.label` (an unreadable
          // concatenation of all 5 method names) any time exactly one area was
          // showing, which is the common case once a method is selected.
          placeholderLabel: allAreas.length > 1 ? (areaLabel || `Area ${i + 1}`) : undefined,
          imagePath:    side.imagePath,
          canvasWidth:  Number.isFinite(width)  && width  > 0 ? width  : 400,
          canvasHeight: Number.isFinite(height) && height > 0 ? height : 400,
          areaLeft:     Number.isFinite(left) ? left : 0,
          areaTop:      Number.isFinite(top)  ? top  : 0,
          designAreas:  [area],
          glbBox:       area.glbBox,
          printColorOptions: side.printColorOptions,
          printPosition: side.printPosition,
          printAreaSizeOptions: side.printAreaSizeOptions,
        };
      });
    });

    setSides(sides);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, activeColorVariantId, selectedPrintMethod, selectedPrintPositions, selectedPrintMethodByPosition, fetchedVariantSides]);
}
